import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  ArrowDownIcon,
  Button,
  H2,
  Paragraph,
  CircleCloseIcon,
} from '@smartshell/ui-kit';

import { LocalizationContext } from 'localization';
import { GET_VERIFICATION_STATUS } from 'schemas';
import {
  EVerificationStatus,
  IGetVerificationStatusRequest,
  IGetVerificationStatusResponse,
} from 'types';

import { IOutGoingCallVerifyProps } from './interfases';
import s from './Verify.module.scss';

export const OutGoingCallVerify: FC<IOutGoingCallVerifyProps> = ({
  phone,
  call_number,
  setIsChangeMethod,
  onPhoneChange,
  verification_id,
  setVerification,
  setOnSuccess,
}) => {
  const { t } = useContext(LocalizationContext);
  const [status, setStatus] = useState<EVerificationStatus>(
    EVerificationStatus.PENDING
  );
  const [seconds, setSeconds] = useState(60);

  const { data } = useQuery<
    IGetVerificationStatusResponse,
    IGetVerificationStatusRequest
  >(GET_VERIFICATION_STATUS, {
    variables: {
      verification_id,
    },
    pollInterval: 5000,
  });

  useEffect(() => {
    if (!data?.getVerificationStatus.status) return;

    if (data.getVerificationStatus.status === EVerificationStatus.COMPLETED) {
      setOnSuccess && setOnSuccess(true);
      setVerification && setVerification(null);
      return;
    }
    setStatus(data.getVerificationStatus.status);
  }, [data]);

  useEffect(() => {
    const id = setInterval(() => {
      setSeconds((state) => state - 1);
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, [seconds < 0]);

  const outGoingCallNumber = useMemo(() => {
    const cleaned = call_number.toString().replace(/\D/g, '');

    if (cleaned.length === 11 && (cleaned[0] === '7' || cleaned[0] === '8')) {
      return `+7 (${cleaned.substring(1, 4)}) ${cleaned.substring(
        4,
        7
      )}-${cleaned.substring(7, 9)}-${cleaned.substring(9)}`;
    }

    return call_number;
  }, [call_number]);

  return (
    <div className={s.outGoingCallVerify}>
      <div className={s.outGoingCallVerify__info}>
        <H2>{t('clientRegister.out_going_call.user_phone', phone)}</H2>
        <ArrowDownIcon />
        <H2>
          {t('clientRegister.out_going_call.call_number', outGoingCallNumber)}
        </H2>
        <Paragraph bold color="secondary" className={s.warning}>
          {t('clientRegister.out_going_call.info')}
        </Paragraph>
      </div>

      {status === EVerificationStatus.FAILED && (
        <div className={s.error}>
          <div className={s['left-content']}>
            <CircleCloseIcon color="error" size="l" />
          </div>
          <div className={s['right-content']}>
            <Paragraph className={s.text}>
              {t('clientRegister.out_going_call.error')}
            </Paragraph>
          </div>
        </div>
      )}

      <div className={s.outGoingCallVerify__controls}>
        <Button
          fullWidth
          onClick={onPhoneChange}
          isLoading={status === EVerificationStatus.SENT}
        >
          {t('back')}
        </Button>
        <Button
          fullWidth
          variant="ghost"
          onClick={() => setIsChangeMethod(true)}
          disabled={seconds > 0}
        >
          {seconds > 0
            ? t('clientRegister.other_type.pending', seconds)
            : t('clientRegister.other_type')}
        </Button>
      </div>
    </div>
  );
};
